import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./BeverageV3.module.css";
import { useLayoutStore } from "~/store";
import {
  SignInDialogUi,
  DialogUi,
  ConsentPopup,
  CharacterCount,
  InvalidCharactersError,
} from "~/components";
import { useNavigate, useLocation } from "react-router-dom";
import { useAcid } from "../../hooks/useAcid";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import morningIcon from "../../assets/beverage/morning.png";
import afternoonIcon from "../../assets/beverage/afternoon.png";
import eveningIcon from "../../assets/beverage/night.png";
import anytimeIcon from "../../assets/beverage/anytime.png";
import checkIcon from "../../assets/beverage/check.png";
import { MAX_BEVERAGE_DESCRIPTION_LENGTH } from "../../utils/beverageDescriptionLimit";
import { hasDisallowedCharacters } from "../../utils/beverageTextValidation";
import {
  sendAnalyticsPageViewEvent,
  sendPageViewWhenConsentReady,
} from "../../utils/adobeDataLayerUtils";
import { transformAiBeverageData } from "../../utils/transformAiBeverageData";
import { mergeStructuredBeverageDataByCategory } from "../../utils/beveragePayload";
import { stripAcidFromSearch } from "../../utils/postLoginRedirect";
import { saveBeverageV3ConsentOnly } from "./consentFlow";

const beverageCategories = [
  {
    name: "Coffee",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/bonvoy-seoul-coffee-14153?wid=316&fit=constrain",
  },
  {
    name: "Tea",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/bonvoy_brand_mbv-1073046?wid=316&fit=constrain",
  },
  {
    name: "Juices",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/cy-selsn-the-22-juice-11454?wid=316&fit=constrain",
  },
  {
    name: "Milk",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/ar-madca-breakfast-milk-00614?wid=316&fit=constrain",
  },
  {
    name: "Water",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/si-mlesi-bottled-water-23199?wid=316&fit=constrain",
  },
  {
    name: "Soda",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/cy-bwibd-soda-market-32168?wid=316&fit=constrain",
  },
  {
    name: "Energy Drink",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/GettyImages-1309892866?wid=316&fit=constrain",
  },
  {
    name: "Beer",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/chifo-beer-1-20903?wid=316&fit=constrain",
  },
  {
    name: "Wine",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/jw-wasjw-white-wine-14577?wid=316&fit=constrain",
  },
  {
    name: "Spirits",
    image:
      "https://cache.marriott.com/is/image/marriotts7prod/br-bodbr-gina-bar-36925?wid=316&fit=constrain",
  },
];

export const BeverageV3 = () => {
  const cid = "beverage";
  const navigate = useNavigate();
  const location = useLocation();
  const { acid } = useAcid();
  const { isResetting, setContentClassName } = useLayoutStore();
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState({});
  const [additionalDetails, setAdditionalDetails] = useState({});
  /**
   * Accumulated structuredBeverageData entries (one per saved category), in
   * the exact shape the save-beverage-preferences action expects.
   */
  const [structuredBeverageData, setStructuredBeverageData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const [isConsentSaving, setIsConsentSaving] = useState(false);
  const [categoryToAutoSave, setCategoryToAutoSave] = useState(null);
  const [consentPopupDismissed, setConsentPopupDismissed] = useState(() => {
    return sessionStorage.getItem("beverageConsentResponded") === "true";
  });
  // Track the user's consent choice for this session (null = not yet set, true = opted out, false = opted in)
  const [sessionOptedOut, setSessionOptedOut] = useState(() => {
    const stored = sessionStorage.getItem("beverageConsentOptedOut");
    return stored !== null ? stored === "true" : null;
  });

  /**
   * Synchronous locks: refs update immediately, unlike the isModalSaving/
   * isSaving state checks below which only block re-entry after React
   * commits a re-render.
   */
  const saveCategoryLockRef = useRef(false);
  const submitLockRef = useRef(false);
  const consentChoiceLockRef = useRef(false);

  useEffect(() => {
    if (isResetting) {
      return;
    }

    setContentClassName(styles.parentContent);
    console.log(
      "BeverageV3 component mounted, categories:",
      beverageCategories.length,
    );
  }, [isResetting, setContentClassName]);

  useEffect(() => {
    return sendPageViewWhenConsentReady("Beverage V3 Page", 'Passions-FavBevs', 'beverages3');
  }, []);

  useEffect(() => {
    if (showSignInDialog) {
      sendAnalyticsPageViewEvent("Beverage V3 Page Modal: Sign In", 'Passions-FavBevs', 'beverages3');
    }
  }, [showSignInDialog]);

  useEffect(() => {
    if (showConsentPopup) {
      sendAnalyticsPageViewEvent("Beverage V3 Page Modal: Consent", 'Passions-FavBevs', 'beverages3');
    }
  }, [showConsentPopup]);

  // Send analytics event when beverage modal opens
  useEffect(() => {
    if (selectedCategory) {
      sendAnalyticsPageViewEvent(
        `Beverage V3 Modal: How do you enjoy your ${selectedCategory.name}?`,
        'Passions-FavBevs',
        'beverages3'
      );
    }
  }, [selectedCategory]);

  // Restore beverage selections from sessionStorage on mount (after login redirect)
  useEffect(() => {
    const savedData = sessionStorage.getItem("pendingBeverageV3Selections");
    if (!savedData) {
      return;
    }

    try {
      const parsed = JSON.parse(savedData);
      const {
        version,
        timestamp,
        sessionId,
        details,
        timeOfDay,
        structuredBeverageData: savedStructuredData,
        pendingSaveCategory,
      } = parsed;

      if (version !== 1) {
        console.warn("⚠️ Incompatible beverage selections version:", version);
        sessionStorage.removeItem("pendingBeverageV3Selections");
        return;
      }

      const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
      const age = Date.now() - timestamp;
      if (age > MAX_AGE_MS) {
        console.warn(
          `⚠️ Beverage selections expired (age: ${Math.round(age / 1000)}s)`,
        );
        sessionStorage.removeItem("pendingBeverageV3Selections");
        return;
      }

      const currentSessionId = sessionStorage.getItem("beverageV3SessionId");
      if (sessionId && currentSessionId && sessionId !== currentSessionId) {
        console.warn("⚠️ Session ID mismatch, ignoring stale selections");
        sessionStorage.removeItem("pendingBeverageV3Selections");
        return;
      }

      setAdditionalDetails(details || {});
      setSelectedTimeOfDay(timeOfDay || {});
      setStructuredBeverageData(savedStructuredData || []);

      if (pendingSaveCategory) {
        setCategoryToAutoSave(pendingSaveCategory);
      }

      sessionStorage.removeItem("pendingBeverageV3Selections");
      console.log(
        `✅ Restored beverage selections (age: ${Math.round(age / 1000)}s, session: ${sessionId})`,
      );
    } catch (error) {
      console.error("❌ Failed to restore beverage selections:", error);
      sessionStorage.removeItem("pendingBeverageV3Selections");
    }
  }, []);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
  };

  const toggleTimeOfDay = (category, timeOfDay) => {
    setSelectedTimeOfDay((prev) => {
      const categoryTimes = prev[category] || [];
      const isSelected = categoryTimes.includes(timeOfDay);

      return {
        ...prev,
        [category]: isSelected
          ? categoryTimes.filter((t) => t !== timeOfDay)
          : [...categoryTimes, timeOfDay],
      };
    });
  };

  /**
   * Saves a single category's preferences (AI-classified from free text) plus
   * whichever consent value applies. Always calls the backend — even with no
   * free text to classify — so a just-answered consent choice still actually
   * reaches CDP instead of only being recorded locally.
   */
  const saveCategoryPreferences = useCallback(
    async (categoryObj, overrideOptedOut) => {
      if (saveCategoryLockRef.current) {
        return false;
      }
      if (isModalSaving) {
        return false; // Prevent double-submission
      }

      saveCategoryLockRef.current = true;
      setIsModalSaving(true);
      setSaveError(null);

      try {
        const description = additionalDetails[categoryObj.name];
        const times = selectedTimeOfDay[categoryObj.name] || [];
        let categoryEntries = [];

        if (description?.trim()) {
          /**
           * Chip-selected times are a deterministic, structured signal —
           * include them in the text sent to the AI as a fallback, but the
           * chip selections themselves always win below regardless of what
           * the AI parses from free text.
           */
          const aiDescription = `${categoryObj.name}: ${description}${
            times.length ? ` at ${times.join(", ")}` : ""
          }`;

          const aiActionUrl =
            actions["marriott-passions-v3/get-beverage-preferences-ai"];
          const aiResponse = await fetch(`${aiActionUrl}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ description: aiDescription }),
          });

          if (!aiResponse.ok) {
            throw new Error("AI processing failed");
          }

          const aiResponseData = await aiResponse.json();
          const aiData = aiResponseData.body || aiResponseData;
          const transformed = transformAiBeverageData(aiData);

          categoryEntries = times.length
            ? transformed.map((entry) => ({
                ...entry,
                types: entry.types.map((typeEntry) => ({
                  ...typeEntry,
                  timeOfDay: times,
                })),
              }))
            : transformed;
        }

        const optedOut =
          overrideOptedOut !== undefined
            ? overrideOptedOut
            : sessionOptedOut !== null
              ? sessionOptedOut
              : false;

        await actionWebInvoke(
          actions["marriott-passions-v3/save-beverage-preferences"],
          {},
          {
            acid,
            structuredBeverageData: categoryEntries,
            optedOutPersonalization: optedOut,
          },
        );

        if (categoryEntries.length) {
          setStructuredBeverageData((prev) =>
            mergeStructuredBeverageDataByCategory(prev, categoryEntries),
          );
        }

        if (selectedCategory && selectedCategory.name === categoryObj.name) {
          handleCloseModal();
        }
        return true;
      } catch (error) {
        console.error("Error processing and saving beverage:", error);
        setSaveError("Failed to save preferences. Please try again.");
        return false;
      } finally {
        setIsModalSaving(false);
        saveCategoryLockRef.current = false;
      }
    },
    [
      acid,
      additionalDetails,
      selectedTimeOfDay,
      isModalSaving,
      selectedCategory,
      sessionOptedOut,
    ],
  );

  const handleSavePreferences = useCallback(async () => {
    if (!acid) {
      setShowSignInDialog(true);
      return;
    }

    if (!consentPopupDismissed) {
      setShowConsentPopup(true);
      return;
    }

    if (selectedCategory) {
      await saveCategoryPreferences(selectedCategory);
    }
  }, [acid, consentPopupDismissed, selectedCategory, saveCategoryPreferences]);

  /**
   * saveCategoryPreferences isn't stably memoized — its own deps change on
   * every save (manual or auto). This ref lets the effects below call the
   * current version without re-running themselves whenever that identity
   * changes.
   */
  const saveCategoryPreferencesRef = useRef(saveCategoryPreferences);
  useEffect(() => {
    saveCategoryPreferencesRef.current = saveCategoryPreferences;
  });

  /**
   * The consent popup can be triggered either by the modal-open manual save
   * (selectedCategory) or by the sign-in-redirect auto-save (categoryToAutoSave,
   * which never opens the modal). Resolve whichever one is actually pending.
   */
  const resolvePendingCategoryObj = useCallback(() => {
    if (selectedCategory) {
      return selectedCategory;
    }
    if (categoryToAutoSave) {
      return (
        beverageCategories.find((c) => c.name === categoryToAutoSave) || null
      );
    }
    return null;
  }, [selectedCategory, categoryToAutoSave]);

  /**
   * Persists the guest's consent choice — via the pending category's save
   * when one exists, otherwise as a standalone consent-only call — and only
   * writes sessionStorage once that save actually succeeds.
   */
  const handleConsentChoice = useCallback(
    async (optedOut) => {
      if (consentChoiceLockRef.current) {
        return;
      }

      consentChoiceLockRef.current = true;
      setIsConsentSaving(true);

      try {
        const categoryObj = resolvePendingCategoryObj();
        const saved = categoryObj
          ? await saveCategoryPreferencesRef.current(categoryObj, optedOut)
          : await saveBeverageV3ConsentOnly({
              acid,
              optedOut,
              actionWebInvoke,
              actionUrl:
                actions["marriott-passions-v3/save-beverage-preferences"],
            });

        if (saved) {
          sessionStorage.setItem("beverageConsentResponded", "true");
          sessionStorage.setItem("beverageConsentOptedOut", String(optedOut));
          setSessionOptedOut(optedOut);
          setConsentPopupDismissed(true);
          setShowConsentPopup(false);
          if (categoryToAutoSave) {
            setCategoryToAutoSave(null);
          }
        }
        /**
         * On failure, leave the popup open so the guest can retry instead of
         * the session silently believing consent was recorded.
         */
      } finally {
        setIsConsentSaving(false);
        consentChoiceLockRef.current = false;
      }
    },
    [resolvePendingCategoryObj, categoryToAutoSave, acid],
  );

  // User opts IN to personalization (NOT opted out)
  const handleConsentAccept = useCallback(async () => {
    await handleConsentChoice(false);
  }, [handleConsentChoice]);

  // User opts OUT of personalization
  const handleConsentDecline = useCallback(async () => {
    await handleConsentChoice(true);
  }, [handleConsentChoice]);

  /**
   * Dismissing the dialog without making a choice defaults to opted out.
   * Unlike accept/decline, closing must always work immediately — an outage
   * or slow network shouldn't leave the guest stuck unable to dismiss this
   * dialog. The opted-out value is still persisted best-effort in the
   * background via handleConsentChoice; if that fails, consent stays
   * unanswered so the popup correctly reappears on the next save attempt.
   */
  const handleConsentClose = useCallback(() => {
    setShowConsentPopup(false);
    handleConsentChoice(true);
  }, [handleConsentChoice]);

  /**
   * Auto-save effect: only acid/categoryToAutoSave actually becoming
   * available should trigger this, nothing else.
   */
  useEffect(() => {
    const autoSave = async () => {
      if (acid && categoryToAutoSave) {
        /**
         * Mirror the manual save's consent gate: don't save with a default
         * consent value the guest never confirmed.
         */
        if (!consentPopupDismissed) {
          setShowConsentPopup(true);
          return;
        }

        const categoryObj = beverageCategories.find(
          (c) => c.name === categoryToAutoSave,
        );
        if (categoryObj) {
          console.log(`🔄 Auto-saving pending category: ${categoryToAutoSave}`);
          /**
           * Clear before awaiting: a re-render mid-request can't see a
           * stale truthy categoryToAutoSave and fire a second save.
           */
          setCategoryToAutoSave(null);
          await saveCategoryPreferencesRef.current(categoryObj);
        }
      }
    };

    autoSave();
  }, [acid, categoryToAutoSave, consentPopupDismissed]);

  const handleSubmit = useCallback(async () => {
    if (!acid) {
      setShowSignInDialog(true);
      return;
    }

    if (submitLockRef.current) {
      return;
    }
    if (isSaving) {
      return; // Prevent double-submission
    }
    submitLockRef.current = true;

    setIsSaving(true);

    try {
      const optedOut =
        sessionOptedOut !== null
          ? sessionOptedOut
          : false;

      await actionWebInvoke(
        actions["marriott-passions-v3/save-beverage-preferences"],
        {},
        {
          acid,
          structuredBeverageData,
          optedOutPersonalization: optedOut,
        },
      );

      console.log("Beverage preferences saved successfully");

      sessionStorage.setItem(
        "savedBeverageV3Preferences",
        JSON.stringify(structuredBeverageData),
      );

      navigate("/beverage3/thank-you");
    } catch (error) {
      console.error("Error saving beverage preferences:", error);
      // TODO: Add error handling UI
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }, [acid, structuredBeverageData, isSaving, navigate, sessionOptedOut]);

  const onCloseDialog = useCallback(
    () => setShowSignInDialog(false),
    [setShowSignInDialog],
  );

  const onSubmitDialog = useCallback(() => {
    let sessionId = sessionStorage.getItem("beverageV3SessionId");
    if (!sessionId) {
      sessionId = `bev3-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("beverageV3SessionId", sessionId);
    }

    const selectionsToSave = {
      version: 1,
      timestamp: Date.now(),
      sessionId,
      details: additionalDetails,
      timeOfDay: selectedTimeOfDay,
      structuredBeverageData,
      pendingSaveCategory: selectedCategory?.name || null,
    };

    try {
      sessionStorage.setItem(
        "pendingBeverageV3Selections",
        JSON.stringify(selectionsToSave),
      );
    } catch (error) {
      console.error("❌ Failed to save beverage selections:", error);
    }
    /**
     * Keep the dialog open until navigation begins, rather than closing it
     * and showing the page loader here — SignInDialogUi's onSubmit runs this
     * callback before it starts the actual redirect.
     */
  }, [additionalDetails, selectedTimeOfDay, structuredBeverageData, selectedCategory]);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.headerTitle}>
            Tap to select your favorite drinks
          </h1>
          <button
            data-testid="beverage-done"
            className={`${styles.doneButton} custom_click_track`}
            data-custom_click_track_value={`Beverage Header|Done|internal`}
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Done"}
          </button>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.categoryGrid}>
          {beverageCategories.map((category, index) => {
            const hasPreferences =
              selectedTimeOfDay[category.name]?.length > 0 ||
              additionalDetails[category.name]?.trim().length > 0;

            return (
              <div
                key={index}
                data-testid="beverage-category"
                className={`${styles.categoryCard} ${hasPreferences ? styles.categoryCardSelected : ""} custom_click_track`}
                data-custom_click_track_value={`Beverages|Category Selection: ${category.name}|internal`}
                onClick={() => handleCategoryClick(category)}
              >
                <div className={styles.categoryImageWrapper}>
                  <img
                    src={category.image}
                    alt={category.name}
                    className={styles.categoryImage}
                    loading="lazy"
                    onError={(e) => {
                      console.error(
                        `Failed to load image for ${category.name}:`,
                        category.image,
                      );
                      e.target.style.backgroundColor = "#f0f0f0";
                    }}
                  />
                  <div className={styles.categoryOverlay}>
                    <span className={styles.categoryName}>{category.name}</span>
                    {hasPreferences && (
                      <img
                        src={checkIcon}
                        alt="Selected"
                        className={styles.checkmarkIcon}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selectedCategory && !showSignInDialog && !showConsentPopup && (
        <DialogUi
          data-testid="beverage-details-dialog"
          onClose={handleCloseModal}
          header={null}
          body={
            <div className={styles.beverageModal}>
              <button
                data-testid="beverage-details-close"
                aria-label={`Close ${selectedCategory.name} preferences`}
                className={`${styles.closeButton} custom_click_track`}
                data-custom_click_track_value={`Beverages|Close Category Modal: ${selectedCategory.name}|internal`}
                onClick={handleCloseModal}
              >
                ✕
              </button>
              <h2 className={styles.modalQuestion}>
                How do you enjoy your {selectedCategory.name.toLowerCase()}?
              </h2>

              <div className={styles.preferencesSection}>
                <h3 className={styles.sectionLabel}>Tell me more</h3>
                <textarea
                  data-testid="beverage-details-input"
                  className={styles.additionalDetailsInput}
                  placeholder="e.g., iced, with cream, extra sweet..."
                  value={additionalDetails[selectedCategory.name] || ""}
                  onChange={(e) =>
                    setAdditionalDetails({
                      ...additionalDetails,
                      [selectedCategory.name]: e.target.value,
                    })
                  }
                  maxLength={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                  rows={2}
                />
                <CharacterCount
                  value={additionalDetails[selectedCategory.name]}
                  max={MAX_BEVERAGE_DESCRIPTION_LENGTH}
                />
                <InvalidCharactersError
                  value={additionalDetails[selectedCategory.name]}
                />

                <p className={styles.hint}>
                  Please do not include profanity, unintelligible information,
                  personally identifiable information, or offensive or
                  inappropriate content.
                </p>
              </div>

              {saveError && (
                <div className={styles.errorMessage}>{saveError}</div>
              )}

              <div className={styles.preferencesSection}>
                <h3 className={styles.sectionLabel}>Time of day (Optional)</h3>
                <div className={styles.timeOfDayGrid}>
                  {[
                    { name: "Morning", icon: morningIcon },
                    { name: "Afternoon", icon: afternoonIcon },
                    { name: "Evening", icon: eveningIcon },
                    { name: "Anytime", icon: anytimeIcon },
                  ].map((time) => {
                    const isSelected = selectedTimeOfDay[
                      selectedCategory.name
                    ]?.includes(time.name);
                    return (
                      <button
                        key={time.name}
                        data-testid="beverage-time-option"
                        aria-pressed={Boolean(isSelected)}
                        className={`${styles.timeOfDayButton} ${isSelected ? styles.timeOfDayButtonSelected : ""} custom_click_track`}
                        data-custom_click_track_value={`Beverages|Selected Category: ${selectedCategory.name}, Time Of Day Selection: ${time.name}|internal`}
                        onClick={() =>
                          toggleTimeOfDay(selectedCategory.name, time.name)
                        }
                      >
                        <img
                          src={time.icon}
                          alt={time.name}
                          className={styles.timeOfDayIcon}
                        />
                        <span className={styles.timeOfDayLabel}>
                          {time.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                data-testid="beverage-details-save"
                className={`${styles.saveButton} custom_click_track`}
                data-custom_click_track_value={`Beverages|Save Category Preferences: ${selectedCategory.name}|internal`}
                onClick={handleSavePreferences}
                disabled={
                  isModalSaving ||
                  hasDisallowedCharacters(additionalDetails[selectedCategory.name])
                }
              >
                {isModalSaving ? "Saving..." : "Save"}
              </button>
            </div>
          }
          footer={null}
          modalStyle={styles.customModalDialog}
          modalBodyStyle={styles.customModalBody}
          modalHeaderStyle={styles.hideDefaultHeader}
          closeButtonStyle={styles.hideDefaultCloseButton}
        />
      )}
      {showSignInDialog && (
        <SignInDialogUi
          onClose={onCloseDialog}
          onSubmit={onSubmitDialog}
          selectedPassions={[]}
          cid={cid}
          redirectUrl={`/beverage3/preferences${stripAcidFromSearch(location.search)}`}
        />
      )}
      {showConsentPopup && (
        <ConsentPopup
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
          onClose={handleConsentClose}
          loading={isConsentSaving}
        />
      )}
    </>
  );
};
